import React, { useEffect, useState } from 'react';

import SpinnerOverlay from '@components/common/SpinnerOverlay';
import Parameters from '@components/Kpi/Parameters';

import { kpi, map } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setKpiSourceClickedState } from '@slices/kpi';

import { IEditPresetNameDTO, IPresetDTO } from '@interfaces/kpi/dto/kpiDTO';
import { ISelectedOnMapSitesByVendor } from '@interfaces/kpi/kpiCommon';
import { IParametersOnMapTypes } from './ParametersOnMap.types';
import { IGroupSelectOptions } from '@components/common/GroupSelectKpi/GroupSelect.types';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import './ParametersOnMap.scss';

const ParametersOnMap = ({
	allSelectedSitesOnMap,
	createPreset,
	deletePreset,
	exportChartsFromParams,
	getCharts,
	isLoadingCharts,
	isLoadingOptions,
	isLoadingPresetForm,
	options4GFddLimenet,
	options4GFddNokia,
	options4GTddLimenet,
	options4GTddNokia,
	options5GLimenet,
	options5GNokia,
	options5GOran,
	optionsLimenetKpi,
	optionsNokiaKpi,
	optionsVendorsKpi,
	presets,
	runProgress,
	setDefaultKpiVendor,
	setVendor,
	updatePreset,
	updatePresetName,
	vendor,
}: IParametersOnMapTypes) => {
	const dispatch = useAppDispatch();
	const { selectedVendorOnSitesCard } = useAppSelector(map);
	const { isKpiSourceClicked, lastKpiSourceData } = useAppSelector(kpi);

	const [isOpenPresetForm, setIsOpenPresetForm] = useState(false);
	const [isDirty, setIsDirty] = useState(false);

	const [presetSaveDate, setPresetSaveDate] = useState<IPresetDTO>();
	const [editablePreset, setEditablePreset] = useState<IEditPresetNameDTO>();
	const [selectedPreset, setSelectedPreset] = useState<IPresetDTO>();
	const [defaultSelectedSitesOnMap, setDefaultSelectedSitesOnMap] = useState<ISelectedOnMapSitesByVendor>();
	const [presetValue, setPresetValue] = useState<TSelectOptionType<string> | null>();

	const togglePresetForm = () => setIsOpenPresetForm(prevState => !prevState);

	const openSavePresetForm = (presetDate: IPresetDTO) => {
		setPresetSaveDate(presetDate);
		togglePresetForm();
	};

	const closePresetForm = () => {
		setPresetSaveDate(undefined);
		setEditablePreset(undefined);
		togglePresetForm();
	};

	const clearPreset = () => setSelectedPreset(undefined);

	useEffect(() => {
		if (!isDirty) return;

		setPresetValue(null);
	}, [isDirty]);

	useEffect(() => {
		if (!allSelectedSitesOnMap?.length || !selectedVendorOnSitesCard?.name) return;

		setDefaultSelectedSitesOnMap(allSelectedSitesOnMap
			.find(sites => sites.vendor === selectedVendorOnSitesCard.name));
	}, [allSelectedSitesOnMap, selectedVendorOnSitesCard]);

	useEffect(() => {
		if (isKpiSourceClicked && lastKpiSourceData) {

			const {
				selectedSiteIds,
				selectedPolygonIds,
				...preset
			} = lastKpiSourceData;

			setSelectedPreset(preset);

			dispatch(setKpiSourceClickedState(false));
		}
	}, [dispatch, isKpiSourceClicked, lastKpiSourceData]);

	return (
		<div className='f5gParametersOnMapContainer'>
			{isLoadingCharts && <SpinnerOverlay/>}
			{isLoadingOptions
				? <SpinnerOverlay/>
				: (
					<Parameters
						allSelectedSitesOnMap={allSelectedSitesOnMap}
						clearPreset={clearPreset}
						closePresetNameForm={closePresetForm}
						createPreset={createPreset}
						defaultSelectedSitesOnMap={defaultSelectedSitesOnMap}
						deletePreset={deletePreset}
						exportChartsFromParams={exportChartsFromParams}
						getCharts={getCharts}
						initialVendor={vendor}
						isApplyTechnologyFromPreset={isKpiSourceClicked && !!lastKpiSourceData}
						isLoadingOptions={isLoadingOptions}
						isLoadingPresetForm={isLoadingPresetForm}
						isMap
						isOpenRenamePresetForm={isOpenPresetForm}
						openPresetForm={openSavePresetForm}
						options4GFddLimenet={options4GFddLimenet}
						options4GFddNokia={options4GFddNokia}
						options4GTddLimenet={options4GTddLimenet}
						options4GTddNokia={options4GTddNokia}
						options5GLimenet={options5GLimenet}
						options5GNokia={options5GNokia}
						options5GOran={options5GOran}
						optionsLimenetKpi={optionsLimenetKpi as IGroupSelectOptions[]}
						optionsNokiaKpi={optionsNokiaKpi as IGroupSelectOptions[]}
						optionsVendorsKpi={optionsVendorsKpi}
						presetSaveDate={presetSaveDate}
						presetValue={presetValue}
						presets={presets}
						renamablePreset={editablePreset}
						runProgress={runProgress}
						selectedPreset={selectedPreset}
						setDefaultKpiVendor={setDefaultKpiVendor}
						setIsDirty={setIsDirty}
						setPresetValue={setPresetValue}
						setRenamablePreset={setEditablePreset}
						setStateSelectedPreset={setSelectedPreset}
						setVendor={setVendor}
						togglePresetNameForm={togglePresetForm}
						updatePreset={updatePreset}
						updatePresetName={updatePresetName}
					/>
				)
			}
		</div>
	);
};

export default ParametersOnMap;
